import React from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Highlighter,
  Undo2,
  Redo2,
  ListChecks,
  Smile,
  Copy,
} from "lucide-react";
import { Editor } from "@tiptap/react";
import { Popover, PopoverTrigger, PopoverContent } from "@shared/components/ui/popover";
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { toast } from '@shared/components/ui/sonner';

interface MiniToolbarProps {
  editor: Editor;
}

const iconButton = (active: boolean) =>
  `p-2 rounded-full hover:bg-accent transition-colors relative flex items-center justify-center ${active ? "toolbar-icon--active hintword-orenge bg-orange-50" : "text-foreground"}`;

export const MiniToolbar: React.FC<MiniToolbarProps> = ({ editor }) => {
  if (!editor) return null;
  const [showEmoji, setShowEmoji] = React.useState(false);

  // Custom copy handler with whitespace cleanup
  const handleCopy = async () => {
    try {
      const { from, to } = editor.state.selection;
      if (from === to) {
        // No selection, copy all content
        const allText = editor.getText();
        const cleanedText = cleanTextForCopy(allText);
        await navigator.clipboard.writeText(cleanedText);
        toast.success('All content copied');
      } else {
        // Copy selected content
        const selectedText = editor.state.doc.textBetween(from, to, '\n');
        const cleanedText = cleanTextForCopy(selectedText);
        await navigator.clipboard.writeText(cleanedText);
        toast.success('Selected text copied');
      }
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy text');
    }
  };

  // Clean text for copying (remove excessive whitespace)
  const cleanTextForCopy = (text: string): string => {
    return text
      .replace(/\n\s*\n\s*\n\s*\n+/g, '\n\n') // Replace 4+ consecutive newlines with 2
      .replace(/^\s+|\s+$/g, '') // Trim start and end
      .replace(/\n\s{2,}/g, '\n ') // Replace multiple spaces after newline with single space
      .replace(/\s{2,}\n/g, ' \n'); // Replace multiple spaces before newline with single space
  };
  return (
    <div className="flex gap-1 mb-2 px-2 py-1 w-fit">
      <button
        type="button"
        aria-label="Bold"
        className={iconButton(editor.isActive("bold"))}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={12} />
      </button>
      <button
        type="button"
        aria-label="Italic"
        className={iconButton(editor.isActive("italic"))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={12} />
      </button>
      <button
        type="button"
        aria-label="Underline"
        className={iconButton(editor.isActive("underline"))}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline size={12} />
      </button>
      <button
        type="button"
        aria-label="Strike"
        className={iconButton(editor.isActive("strike"))}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough size={12} />
      </button>
      <button
        type="button"
        aria-label="Code"
        className={iconButton(editor.isActive("code"))}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code size={12} />
      </button>
      <button
        type="button"
        aria-label="Bullet List"
        className={iconButton(editor.isActive("bulletList"))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={12} />
      </button>
      <button
        type="button"
        aria-label="Ordered List"
        className={iconButton(editor.isActive("orderedList"))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={12} />
      </button>
      <button
        type="button"
        aria-label="Task List"
        className={iconButton(editor.isActive("taskList"))}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <ListChecks size={12} />
      </button>
      <button
        type="button"
        aria-label="Highlight"
        className={iconButton(editor.isActive("highlight"))}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter size={12} />
      </button>
      <button
        type="button"
        aria-label="Undo"
        className={iconButton(false)}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 size={12} />
      </button>
      <button
        type="button"
        aria-label="Redo"
        className={iconButton(false)}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 size={12} />
      </button>
      <button
        type="button"
        aria-label="Copy"
        className={iconButton(false)}
        onClick={handleCopy}
      >
        <Copy size={12} />
      </button>
      <Popover open={showEmoji} onOpenChange={setShowEmoji}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Insert Emoji"
            className={iconButton(false)}
          >
            <Smile size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0">
          <Picker
            data={data}
            theme="auto"
            previewPosition="none"
            onEmojiSelect={(emoji: any) => {
              editor.chain().focus().insertContent(emoji.native).run();
              setShowEmoji(false);
            }}
            skinTonePosition="none"
            searchPosition="sticky"
            style={{ width: 320, border: 'none', boxShadow: 'none' }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}; 